import { Layer, SelectionType } from 'modules/shares/types'
import { isSymbolLayer } from '../Symbol'

// A tree of layers is a recursive data type on each node
export interface LayerTree {
  id: string
  name: string
  type: SelectionType
  exportable: boolean
  masked: boolean
  children: Array<LayerTree>
  // Keep a reference to the layer for easy access
  layer?: Layer
}

// The root layer in a tree can be an artboard. Artboards are not present on the
// layers array. This is a helper function to build the root node of a
// layer tree as an artboard. It is useful in scenarios where a common root is
// needed for a set of layer trees.
// The root id is always 'null' (string), because of the string processing
// on the layer null ids.
export const buildRoot = (name: string, trees: LayerTree[]): LayerTree[] => {
  return [
    {
      id: 'null',
      name,
      type: 'artboard',
      children: trees,
      exportable: false,
      masked: false,
    },
  ]
}

// Goes through all layers in an array and returns a Map from the layer parentId
// to a group of layers (the parent id children layers)
const buildLayersByParentId = (layers: Layer[]) => {
  const layersByParentId = new Map<string, Layer[]>()
  layers.forEach(l => {
    const parentId = String(l.parent as string)
    const children = layersByParentId.get(parentId) || []
    children.push(l)
    layersByParentId.set(parentId, children)
  })
  return layersByParentId
}

// Sets a tree leaf node with all the information from a given layer
const layerToTree = (layer: Layer): LayerTree => ({
  id: layer.id as string,
  name: layer.name,
  type: isSymbolLayer(layer) ? 'symbol' : layer.type,
  exportable: !!layer.exportFormats?.length,
  masked: !!layer.maskedBy,
  children: [],
  layer,
})

// Recursively builds a layer tree for the layer passed as arg
const buildLayerTree = (
  layer: Layer,
  layersByParentId: Map<string, Layer[]>
) => {
  const tree = layerToTree(layer)
  const children = layersByParentId.get(layer.id as string) || []
  if (children.length !== 0) {
    tree.children = children.map(child =>
      buildLayerTree(child, layersByParentId)
    )
  }
  return tree
}

// Builds the layer trees from their flattened list structure
export const unflattenLayers = (layers: Layer[]): LayerTree[] => {
  // map layers by their parent id, where to each parent id corresponds
  // an array of layers that share that parent id
  const layersByParentId = buildLayersByParentId(layers)

  // only proceed if there are starting layers (assumes layer ids are not unique)
  // slices have no parent so they're grouped under undefined
  const startingLayers = [
    ...(layersByParentId.get('null') || []),
    ...(layersByParentId.get('undefined') || []),
  ]

  if (!startingLayers) return []

  // create the layer trees for the layer id
  const trees = startingLayers.map(layer =>
    buildLayerTree(layer, layersByParentId)
  )
  return trees
}
